import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  CircularProgress,
  Typography,
  TablePagination,
  Chip,
  InputAdornment,
  IconButton,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import userService from '../../services/userService'
import { User } from '../../services/userService'
import CommonPagination from '../common/CommonPagination'
interface AddProjectUsersDialogProps {
  open: boolean
  onClose: () => void
  projectId: string
  onAddUsers: (users: { username: string; role: string }[]) => Promise<void>
}

const AddProjectUsersDialog: React.FC<AddProjectUsersDialogProps> = ({
  open,
  onClose,
  projectId,
  onAddUsers,
}) => {
  const [users, setUsers] = useState<User[]>([])
  const [selectedUsers, setSelectedUsers] = useState<{
    [key: string]: boolean
  }>({})
  const [loading, setLoading] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(6)
  const [usersPagination, setUsersPagination] = useState({
    total: 0,
    page: 1,
    perPage: 6,
    totalPages: 1,
  })

  useEffect(() => {
    if (open) {
      fetchUsers()
    }
  }, [open, page, rowsPerPage])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const params = {
        page: page + 1, // Convert from 0-based to 1-based pagination
        perPage: rowsPerPage,
        search: searchKeyword || undefined,
      }

      const { users: fetchedUsers, pagination } = await userService.getUsers(
        params,
      )
      setUsers(fetchedUsers)
      setUsersPagination(pagination)
      setLoading(false)
    } catch (error) {
      console.error('Error fetching users:', error)
      setLoading(false)
    }
  }

  const handleSearch = async () => {
    setPage(0) // Reset to first page on search
    try {
      setLoading(true)
      const params = {
        page: 1,
        perPage: rowsPerPage,
        search: searchKeyword,
      }

      const { users: fetchedUsers, pagination } = await userService.searchUsers(
        searchKeyword,
        params,
      )
      setUsers(fetchedUsers)
      setUsersPagination(pagination)
      setLoading(false)
    } catch (error) {
      console.error('Error searching users:', error)
      setLoading(false)
    }
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleToggleUser = (userId: string) => {
    setSelectedUsers((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }))
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected: { [key: string]: boolean } = {}
      users.forEach((user) => {
        newSelected[user._id] = true
      })
      setSelectedUsers(newSelected)
    } else {
      setSelectedUsers({})
    }
  }

  const handleAddUsers = async () => {
    const selectedUsersList = users.filter((user) => selectedUsers[user._id])

    if (selectedUsersList.length === 0) {
      return
    }

    const usersToAdd = selectedUsersList.map((user) => ({
      username: user.username,
      role: 'member',
    }))

    await onAddUsers(usersToAdd)
    onClose()
  }

  const convertRoleToText = (role: string) => {
    switch (role) {
      case 'admin':
        return 'ผู้ดูแลระบบ'
      case 'user':
        return 'ผู้ใช้งาน'
      case 'employee':
        return 'พนักงาน'
      default:
        return role
    }
  }

  const isAllSelected =
    users.length > 0 && users.every((user) => selectedUsers[user._id])
  const selectedCount = Object.values(selectedUsers).filter(Boolean).length

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">เพิ่มสมาชิกในโครงการ</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <TextField
            fullWidth
            placeholder="ค้นหาผู้ใช้"
            variant="outlined"
            size="small"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} edge="end">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={300}
          >
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={300}
          >
            <Typography variant="body1" color="text.secondary">
              ไม่พบข้อมูลผู้ใช้
            </Typography>
          </Box>
        ) : (
          <Paper variant="outlined">
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedCount > 0 && selectedCount < users.length
                        }
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>ชื่อผู้ใช้</TableCell>
                    <TableCell>ชื่อ-นามสกุล</TableCell>
                    <TableCell>เบอร์โทรศัพท์</TableCell>
                    <TableCell>ประเภทผู้ใช้งาน</TableCell>
                    <TableCell>สถานะการใช้งาน</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow
                      key={user._id}
                      hover
                      onClick={() => handleToggleUser(user._id)}
                      selected={selectedUsers[user._id]}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={!!selectedUsers[user._id]} />
                      </TableCell>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.name || '-'}</TableCell>
                      <TableCell>{user.phoneNumber || '-'}</TableCell>
                      <TableCell>
                        {user.roles.map((role) => (
                          <Chip
                            key={role}
                            label={convertRoleToText(role)}
                            size="small"
                            color={role === 'admin' ? 'primary' : 'default'}
                            sx={{ mr: 0.5 }}
                          />
                        ))}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.isEnable ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                          size="small"
                          color={user.isEnable ? 'success' : 'error'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {/* <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={usersPagination.total}
              rowsPerPage={rowsPerPage}
              page={usersPagination.page > 0 ? usersPagination.page - 1 : 0}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="แถวต่อหน้า:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} จาก ${count}`
              }
            /> */}
            <CommonPagination
              count={Math.ceil(usersPagination.total / rowsPerPage)}
              page={usersPagination.page}
              rowsPerPage={rowsPerPage}
              onChange={(event, value) => {
                handleChangePage(event, value)
              }}
              onRowsPerPageChange={(value) => {
                handleChangeRowsPerPage({
                  target: { value: value.toString() },
                } as React.ChangeEvent<HTMLInputElement>)
              }}
              totalItems={usersPagination.total}
            />
          </Paper>
        )}
      </DialogContent>
      <DialogActions>
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          px={2}
          py={1}
        >
          <Typography variant="body2">เลือก {selectedCount} รายการ</Typography>
          <Box>
            <Button onClick={onClose} color="inherit">
              ยกเลิก
            </Button>
            <Button
              onClick={handleAddUsers}
              color="primary"
              variant="contained"
              disabled={selectedCount === 0}
            >
              เพิ่มสมาชิก
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default AddProjectUsersDialog
